import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Stack,
  NumberInput,
  NumberInputField,
  Text,
  Checkbox,
  Image,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getMenuImageUrl, getMenuImagePlaceholderUrl } from '../../../utils/menuImageUrl';

export default function ProductForm({
  initialValue,
  onSubmit,
  onCancel,
  isSubmitting,
  existingCategories = [],
  allProducts = [],
}) {
  const generateRandomId = () => {
    return Math.random().toString(36).slice(2, 12);
  };
  const [values, setValues] = useState({
    category: '',
    id: '',
    title: '',
    descriptionShort: '',
    description: '',
    price: '',
    vat: '10',
    maxQuantity: '',
    extra: '',
    discountEvery: '',
    discountGet: '',
    discountFrom: null,
    discountTo: null,
    imageId: '',
  });
  const [isComboOnly, setIsComboOnly] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const fileInputRef = useRef(null);
  const [previewNonce, setPreviewNonce] = useState(0);

  useEffect(() => {
    if (initialValue) {
      let discountEvery = '';
      let discountGet = '';
      let discountFrom = null;
      let discountTo = null;
      const rawDiscount = initialValue.discount;
      if (rawDiscount) {
        let d = rawDiscount;
        if (typeof d === 'string') {
          try {
            d = JSON.parse(d);
          } catch (e) {
            d = null;
          }
        }
        if (d && typeof d === 'object') {
          if (d.every !== undefined) {
            discountEvery = String(d.every);
          }
          if (d.get !== undefined) {
            discountGet = String(d.get);
          }
          if (d.from) {
            const f =
              typeof d.from.toDate === 'function'
                ? d.from.toDate()
                : d.from instanceof Date
                ? d.from
                : new Date(d.from);
            if (!Number.isNaN(f.getTime())) {
              discountFrom = f;
            }
          }
          if (d.to) {
            const t =
              typeof d.to.toDate === 'function'
                ? d.to.toDate()
                : d.to instanceof Date
                ? d.to
                : new Date(d.to);
            if (!Number.isNaN(t.getTime())) {
              discountTo = t;
            }
          }
        }
      }

      setValues({
        category: initialValue.category || '',
        id: initialValue.id || '',
        title: initialValue.title || '',
        descriptionShort: initialValue.descriptionShort || '',
        description: initialValue.description || '',
        price: initialValue.price !== undefined ? String(initialValue.price) : '',
        vat:
          initialValue.vat !== undefined
            ? String(initialValue.vat)
            : initialValue.iva !== undefined
            ? String(initialValue.iva)
            : '10',
        maxQuantity:
          initialValue.maxQuantity !== undefined ? String(initialValue.maxQuantity) : '',
        extra: initialValue.extra || '',
        discountEvery,
        discountGet,
        discountFrom,
        discountTo,
        imageId: initialValue.imageId || '',
      });
      setIsComboOnly(initialValue.extra === 'COMBO_ONLY');
    }
  }, [initialValue]);

  useEffect(() => {
    if (!initialValue) {
      setValues((prev) => {
        if (prev.id) return prev;
        return { ...prev, id: generateRandomId() };
      });
    }
  }, [initialValue]);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(values);
  };

  const previewUrl = values.id
    ? getMenuImageUrl({
        imageId: values.imageId || undefined,
        id: values.id,
        kind: 'product',
        query: values.imageId ? `v=${previewNonce}` : undefined,
      })
    : getMenuImagePlaceholderUrl();

  const handleChangeImageClick = () => {
    if (!values.id) {
      setImageError('Si è verificato un problema con l’ID del prodotto');
      return;
    }
    setImageError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    setImageError('');

    if (!(file.type === 'image/jpeg' || file.type === 'image/pjpeg')) {
      setImageError('Sono consentiti solo file JPG/JPEG');
      e.target.value = null;
      return;
    }

    setUploadingImage(true);
    try {
      const { uploadProductImage } = await import('../../../api');
      const result = await uploadProductImage(file, values.id, values.imageId);
      setValues((prev) => ({
        ...prev,
        imageId: result.imageId,
      }));
      setPreviewNonce((n) => n + 1);
    } catch (err) {
      setImageError(err?.message || 'Errore durante il caricamento dell’immagine');
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <Box as='form' onSubmit={handleSubmit}>
      <Stack spacing={4}>
        <FormControl isRequired>
          <FormLabel>Categoria</FormLabel>
          <Input
            value={values.category}
            onChange={handleChange('category')}
            placeholder='Seleziona o scrivi una nuova categoria'
            list='menu-admin-product-categories'
          />
          <datalist id='menu-admin-product-categories'>
            {existingCategories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          {values.category &&
            !existingCategories.includes(values.category) && (
              <Text mt={1} fontSize='sm' color='yellow.300'>
                Stai creando una nuova categoria
              </Text>
            )}
        </FormControl>
        <FormControl>
          <FormLabel>Immagine</FormLabel>
          <Stack direction='row' spacing={4} align='center'>
            <Image
              src={previewUrl}
              fallbackSrc={getMenuImagePlaceholderUrl()}
              boxSize='96px'
              objectFit='cover'
              borderRadius={6}
              border='1px solid #444'
              bg='#0f365f'
            />
            <Stack spacing={1}>
              <Button size='sm' onClick={handleChangeImageClick} isDisabled={uploadingImage}>
                Cambia
              </Button>
              {uploadingImage && (
                <Text fontSize='xs' color='gray.300'>
                  Caricamento immagine...
                </Text>
              )}
              {imageError && (
                <Text fontSize='xs' color='red.300'>
                  {imageError}
                </Text>
              )}
            </Stack>
          </Stack>
          <input
            ref={fileInputRef}
            type='file'
            accept='image/jpeg,image/jpg'
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Titolo</FormLabel>
          <Input value={values.title} onChange={handleChange('title')} />
        </FormControl>
        <FormControl>
          <FormLabel>Descrizione breve</FormLabel>
          <Input value={values.descriptionShort} onChange={handleChange('descriptionShort')} />
        </FormControl>
        <FormControl>
          <FormLabel>Descrizione lunga</FormLabel>
          <Textarea value={values.description} onChange={handleChange('description')} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Prezzo (€)</FormLabel>
          <NumberInput
            min={0}
            precision={2}
            value={values.price}
            onChange={(valueString) => {
              setValues((prev) => ({ ...prev, price: valueString }));
            }}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl borderWidth={1} borderRadius={8} borderColor='gray.600' p={4}>
          <FormLabel mb={2}>Scontistica</FormLabel>
          <Stack direction='row' spacing={3} align='center'>
            <Box>
              <Text fontSize='sm' mb={1}>
                Ogni
              </Text>
              <NumberInput
                min={1}
                value={values.discountEvery}
                onChange={(valueString) => {
                  setValues((prev) => ({ ...prev, discountEvery: valueString }));
                }}
              >
                <NumberInputField placeholder='X' />
              </NumberInput>
            </Box>
            <Box>
              <Text fontSize='sm' mb={1}>
                Dai
              </Text>
              <NumberInput
                min={1}
                value={values.discountGet}
                onChange={(valueString) => {
                  setValues((prev) => ({ ...prev, discountGet: valueString }));
                }}
              >
                <NumberInputField placeholder='Y' />
              </NumberInput>
            </Box>
          </Stack>
          <Stack direction='row' spacing={3} mt={3}>
            <Box>
              <Text fontSize='sm' mb={1}>
                Valido da
              </Text>
              <DatePicker
                selected={values.discountFrom}
                onChange={(d) => {
                  setValues((prev) => ({ ...prev, discountFrom: d }));
                }}
                dateFormat='dd/MM/yyyy'
              />
            </Box>
            <Box>
              <Text fontSize='sm' mb={1}>
                al
              </Text>
              <DatePicker
                selected={values.discountTo}
                onChange={(d) => {
                  setValues((prev) => ({ ...prev, discountTo: d }));
                }}
                dateFormat='dd/MM/yyyy'
              />
            </Box>
          </Stack>
        </FormControl>
        <FormControl>
          <FormLabel>IVA (%)</FormLabel>
          <NumberInput min={0} max={100} defaultValue={10}>
            <NumberInputField value={values.vat} onChange={handleChange('vat')} />
          </NumberInput>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Quantità massima</FormLabel>
          <NumberInput
            min={0}
            value={values.maxQuantity}
            onChange={(valueString) => {
              setValues((prev) => ({ ...prev, maxQuantity: valueString }));
            }}
          >
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl>
          <Checkbox
            isChecked={isComboOnly}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsComboOnly(checked);
              setValues((prev) => ({
                ...prev,
                extra: checked ? 'COMBO_ONLY' : '',
              }));
            }}
          >
            Disponible solo per combo
          </Checkbox>
        </FormControl>
        <Stack direction='row' spacing={3} justify='flex-end' pt={2}>
          <Button  onClick={onCancel}>
            Annulla
          </Button>
          <Button type='submit' isLoading={isSubmitting}>
            Salva
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

